import { useCallback, useEffect, useRef, useState } from 'react'

const PREFERENCE_KEY = 'roulette-sound'

export function useRouletteSound() {
  const [enabled, setEnabled] = useState(() => {
    try {
      return localStorage.getItem(PREFERENCE_KEY) !== 'off'
    } catch {
      return true
    }
  })
  const enabledRef = useRef(enabled)
  const context = useRef<AudioContext | null>(null)
  const master = useRef<GainNode | null>(null)

  useEffect(
    () => () => {
      if (context.current && context.current.state !== 'closed')
        void context.current.close().catch(() => {})
      context.current = null
      master.current = null
    },
    []
  )

  // Called directly by the gesture, before the spin API is awaited (including iOS).
  const unlock = useCallback(() => {
    if (!enabledRef.current) return
    try {
      const Audio =
        window.AudioContext ??
        (window as typeof window & { webkitAudioContext?: typeof AudioContext })
          .webkitAudioContext
      if (!Audio) return
      if (!context.current || context.current.state === 'closed') {
        context.current = new Audio()
        master.current = context.current.createGain()
        master.current.gain.value = 0.45
        master.current.connect(context.current.destination)
      }
      void context.current.resume().catch(() => {})
    } catch {
      /* Playback is optional on restricted devices. */
    }
  }, [])

  const tone = useCallback(
    (
      frequency: number,
      delay: number,
      duration: number,
      volume: number,
      type: OscillatorType = 'sine'
    ) => {
      const audio = context.current
      if (
        !enabledRef.current ||
        !audio ||
        !master.current ||
        audio.state !== 'running' ||
        document.hidden
      )
        return
      try {
        const oscillator = audio.createOscillator()
        const envelope = audio.createGain()
        const start = audio.currentTime + delay
        oscillator.type = type
        oscillator.frequency.setValueAtTime(frequency, start)
        oscillator.frequency.exponentialRampToValueAtTime(
          frequency * 0.85,
          start + duration
        )
        envelope.gain.setValueAtTime(0, start)
        envelope.gain.linearRampToValueAtTime(volume, start + 0.004)
        envelope.gain.exponentialRampToValueAtTime(0.0001, start + duration)
        oscillator.connect(envelope)
        envelope.connect(master.current)
        oscillator.start(start)
        oscillator.stop(start + duration + 0.02)
        oscillator.onended = () => {
          oscillator.disconnect()
          envelope.disconnect()
        }
      } catch {
        /* A device can suspend audio while a spin is in progress. */
      }
    },
    []
  )

  const tick = useCallback(() => {
    tone(720, 0, 0.045, 0.09, 'triangle')
  }, [tone])
  const finish = useCallback(
    (won: boolean) => {
      const notes = won ? [523.25, 659.25, 783.99, 1046.5] : [392, 329.63]
      notes.forEach((frequency, index) =>
        tone(frequency, index * 0.105, won ? 0.55 : 0.24, won ? 0.12 : 0.065)
      )
      if (won) tone(523.25, 0.32, 0.8, 0.055, 'triangle')
    },
    [tone]
  )
  const toggle = useCallback(() => {
    const next = !enabledRef.current
    enabledRef.current = next
    setEnabled(next)
    if (master.current && context.current)
      master.current.gain.setTargetAtTime(
        next ? 0.45 : 0,
        context.current.currentTime,
        0.015
      )
    try {
      localStorage.setItem(PREFERENCE_KEY, next ? 'on' : 'off')
    } catch {
      /* Private storage may be unavailable. */
    }
    if (next) unlock()
  }, [unlock])

  return { enabled, toggle, unlock, tick, finish }
}
